import { useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { addDays, format, startOfDay } from "date-fns"
import {
	IconCheckOutline18 as Check,
	IconChevronDownOutline18 as ChevronDown,
} from "nucleo-ui-outline-18"

import type { TeamMember } from "@productlane/zero/queries"
import { isOutOfOffice } from "@productlane/lib/src/out-of-office"
import { Avatar, Button, Popover } from "@productlane/ui"
import { Calendar } from "@productlane/ui/src/components/internal/calendar"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

export const formatBackOn = (date: Date | number) => format(date, "EEE, d MMM")

export const defaultBackOn = () => startOfDay(addDays(new Date(), 7))

export const memberName = (member: TeamMember | null | undefined) =>
	member?.user?.name ?? member?.user?.email ?? ""

/** Teammates who can cover: not you, not a viewer, not away themselves. */
export function useCovers(userId: string | undefined) {
	const [teamMembers] = useQuery(queries.teamMembers())
	return useMemo(
		() =>
			teamMembers.filter(
				(member) =>
					member.userId !== userId &&
					member.role !== "VIEWER" &&
					!isOutOfOffice(member.outOfOfficeUntil),
			),
		[teamMembers, userId],
	)
}

type Align = "start" | "end"

export function BackOnPicker({
	value,
	onChange,
	align = "end",
}: {
	value: Date
	onChange: (value: Date) => void
	align?: Align
}) {
	const [isOpen, setIsOpen] = useState(false)
	return (
		<Popover
			open={isOpen}
			onOpenChange={setIsOpen}
			className="w-auto bg-white p-0 dark:bg-gray-800"
			align={align}
			content={
				<Calendar
					mode="single"
					selected={value}
					disabled={{ before: addDays(new Date(), 1) }}
					onSelect={(selected) => {
						if (selected) onChange(startOfDay(selected))
						setIsOpen(false)
					}}
					autoFocus
					required
				/>
			}
		>
			<Button type="button" variant="outline" iconRight={ChevronDown}>
				{formatBackOn(value)}
			</Button>
		</Popover>
	)
}

export function CoverPicker({
	covers,
	cover,
	onChange,
	align = "end",
}: {
	covers: readonly TeamMember[]
	cover: TeamMember | null
	onChange: (userId: string) => void
	align?: Align
}) {
	const [isOpen, setIsOpen] = useState(false)
	const coverName = memberName(cover)
	return (
		<Popover
			open={isOpen}
			onOpenChange={setIsOpen}
			className="p-0"
			align={align}
			content={
				<Command
					filter={(_, search, keywords) =>
						keywords?.some((keyword) =>
							keyword.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
						)
							? 1
							: 0
					}
				>
					<CommandInput autoFocus placeholder="Choose teammate..." />
					<CommandList>
						<CommandEmpty>No matching teammates found.</CommandEmpty>
						<CommandGroup>
							{covers.map((member) => (
								<CommandItem
									key={member.id}
									value={member.id}
									keywords={[memberName(member)]}
									onSelect={() => {
										onChange(member.userId)
										setIsOpen(false)
									}}
								>
									<div className="flex w-full items-center gap-2">
										<Avatar
											image={member.user?.imageUrl}
											name={member.user?.name ?? ""}
											size="xs"
										/>
										<span className="flex-1 truncate">
											{memberName(member)}
										</span>
										{cover?.userId === member.userId && (
											<Check className="h-4 w-4 flex-none" />
										)}
									</div>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			}
		>
			<Button
				type="button"
				variant="outline"
				iconRight={ChevronDown}
				className="max-w-52"
			>
				{cover ? (
					<>
						<Avatar
							image={cover.user?.imageUrl}
							name={coverName}
							size="xxs"
							className="mr-2"
						/>
						<span className="truncate">{coverName}</span>
					</>
				) : (
					"Choose teammate"
				)}
			</Button>
		</Popover>
	)
}
